import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';
import { API_URL, formatDate } from '@/lib/utils';
import { Calendar, User, ArrowLeft, Eye, BookOpen, ArrowRight, Tag } from 'lucide-react';

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const [blogRes, allBlogsRes] = await Promise.all([
          axios.get(`${API_URL}/blogs/${slug}`),
          axios.get(`${API_URL}/blogs?published_only=true`)
        ]);
        setBlog(blogRes.data);
        const related = allBlogsRes.data
          .filter(b => b.category === blogRes.data.category && b.slug !== slug)
          .slice(0, 3);
        setRelatedBlogs(related);
      } catch (error) {
        console.error('Error fetching blog:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" data-testid="blog-not-found">
        <BookOpen className="h-16 w-16 text-gray-300 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Blog Post Not Found</h1>
        <p className="text-gray-600 mb-6">The article you're looking for doesn't exist or has been removed.</p>
        <Button asChild className="bg-[#1a2a5e] hover:bg-[#2d4a8e]">
          <Link to="/blog">Back to Blog</Link>
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="blog-detail-page">
      <SEO
        title={blog.title}
        description={blog.excerpt}
        keywords={blog.tags?.join(', ')}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/blog" className="inline-flex items-center text-[#1a2a5e] hover:text-[#2eb24c] mb-6 text-sm font-medium">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Link>

          <span className="inline-block text-xs font-semibold text-[#2eb24c] bg-[#2eb24c]/10 px-3 py-1 rounded-full mb-4">
            {blog.category}
          </span>

          <h1 className="font-manrope text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a2a5e] mb-6 leading-tight">
            {blog.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <User className="h-4 w-4" />
              {blog.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {formatDate(blog.created_at)}
            </span>
            <span className="flex items-center gap-1.5">
              <Eye className="h-4 w-4" />
              {blog.views} views
            </span>
          </div>
        </div>
      </section>

      {/* Blog Image */}
      {blog.image_url && (
        <div className="max-w-4xl mx-auto px-4 -mt-2">
          <img
            src={blog.image_url}
            alt={blog.title}
            className="w-full h-[300px] md:h-[400px] object-cover rounded-2xl shadow-lg"
          />
        </div>
      )}

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed whitespace-pre-line" data-testid="blog-content">
            {blog.content}
          </div>

          {/* Tags */}
          {blog.tags && blog.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t">
              <Tag className="h-4 w-4 text-gray-400" />
              {blog.tags.map((tag) => (
                <span key={tag} className="text-xs font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Share / CTA */}
          <div className="mt-10 p-6 bg-slate-50 rounded-xl text-center">
            <h3 className="font-manrope font-bold text-lg text-[#1a2a5e] mb-2">Need Expert Consultation?</h3>
            <p className="text-gray-600 text-sm mb-4">Our healthcare experts are ready to assist you with any questions.</p>
            <Button asChild className="bg-[#1a2a5e] hover:bg-[#2d4a8e]">
              <Link to="/contact" data-testid="blog-cta-btn">
                Get Free Consultation <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedBlogs.length > 0 && (
        <section className="py-12 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="font-manrope text-2xl font-bold text-[#1a2a5e] mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedBlogs.map((related) => (
                <Link key={related.id} to={`/blog/${related.slug}`}>
                  <Card className="h-full border-0 shadow-lg blog-card overflow-hidden">
                    <div className="aspect-video bg-gradient-to-br from-[#1a2a5e] to-[#3b7dd8] flex items-center justify-center">
                      {related.image_url ? (
                        <img src={related.image_url} alt={related.title} className="w-full h-full object-cover" />
                      ) : (
                        <BookOpen className="h-12 w-12 text-white/50" />
                      )}
                    </div>
                    <CardContent className="p-5">
                      <span className="text-xs font-semibold text-[#2eb24c]">{related.category}</span>
                      <h3 className="font-manrope font-bold text-[#1a2a5e] mt-1 mb-2 line-clamp-2">{related.title}</h3>
                      <p className="text-gray-600 text-sm line-clamp-2">{related.excerpt}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetail;
